/**
 * Clients Page
 *
 * Mobile-first client list with cards on mobile, table on desktop.
 */

import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, Search, Users, ChevronRight } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency, getIdentificationTypeLabel } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { useUrlPage, useUrlState } from '@/hooks/useUrlState'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import type { Client } from '@/types'

export function ClientsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useUrlPage()
  const [search, setSearch] = useUrlState('q')

  // Fetch clients
  const { data, isLoading } = useQuery({
    queryKey: ['clients', page, search],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('page', page.toString())
      params.set('page_size', '20')
      if (search) {
        params.set('search', search)
      }
      const response = await api.get(`/clients/?${params}`)
      return response.data
    },
  })

  return (
    <div className="space-y-4">
      {/* Header - Mobile optimized */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold">{t('clients.title')}</h1>
          <p className="text-sm text-muted-foreground hidden md:block">
            {data?.total || 0} {t('clients.title').toLowerCase()}
          </p>
        </div>
        <Button onClick={() => navigate('/clients/new')} className="hidden md:flex">
          <Plus className="h-4 w-4 mr-2" />
          {t('clients.newClient')}
        </Button>
      </div>

      {/* Search - Touch friendly */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          placeholder={t('clients.searchPlaceholder')}
          className="pl-10 h-12 text-base"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : data?.items?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">{t('clients.noClients')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {search ? t('common.noResults') : t('clients.addFirst')}
          </p>
          {!search && (
            <Button onClick={() => navigate('/clients/new')}>
              <Plus className="h-4 w-4 mr-2" />
              {t('clients.newClient')}
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: Cards */}
          <div className="space-y-3 lg:hidden">
            {data?.items?.map((client: Client) => (
              <Link
                key={client.id}
                to={`/clients/${client.id}`}
                className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
              <Card
                className="active:bg-muted/50 cursor-pointer touch-manipulation"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{client.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {client.identification}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {client.current_balance > 0 && (
                        <Badge variant="destructive" className="shrink-0">
                          {formatCurrency(client.current_balance)}
                        </Badge>
                      )}
                      <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              </Link>
            ))}
          </div>

          {/* Desktop: Table */}
          <Card className="hidden lg:block">
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium">{t('clients.name')}</th>
                    <th className="text-left p-4 font-medium">{t('clients.identificationType')}</th>
                    <th className="text-left p-4 font-medium">{t('clients.identification')}</th>
                    <th className="text-left p-4 font-medium">{t('clients.phone')}</th>
                    <th className="text-right p-4 font-medium">{t('deposits.totalDebt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items?.map((client: Client) => (
                    <tr
                      key={client.id}
                      className="border-b hover:bg-muted/50 cursor-pointer focus-visible:outline-none focus-visible:bg-muted/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                      onClick={() => navigate(`/clients/${client.id}`)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') navigate(`/clients/${client.id}`)
                      }}
                    >
                      <td className="p-4 font-medium">{client.name}</td>
                      <td className="p-4">
                        {getIdentificationTypeLabel(client.type_identification)}
                      </td>
                      <td className="p-4">{client.identification}</td>
                      <td className="p-4">{client.phone || '-'}</td>
                      <td className="p-4 text-right">
                        {client.current_balance > 0 ? (
                          <span className="text-red-500 font-medium">
                            {formatCurrency(client.current_balance)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Pagination */}
          {data && data.pages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <p className="text-sm text-muted-foreground">
                {t('common.page')} {data.page} {t('common.of')} {data.pages}
              </p>
              <Pagination
                currentPage={page}
                totalPages={data.pages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
